<template>
  <div
    class="sb-barcode-input-progress"
    :data-sb-automation="dataSbAutomation"
    data-sb-regression="sb-barcode-input-progress"
  >
    <input
      v-bind="$attrs"
      ref="input"
      type="text"
      class="sb-input"
      :value="modelValue || value"
      data-test-id="barcode-input"
      :style="{'background-image': `url(${barcodeImage})`}"
      @input="updateValue"
      @keyup.enter="onPressEnter"
    >
    <span
      class="quantity-label"
      data-test-id="quantity-label"
    >{{ quantityLabel }}</span>
    <strong
      class="quantity-value"
      data-test-id="quantity-value"
    >{{ quantityValue }}</strong>
    <sb-progress-bar
      :value="progress"
      :rounded="false"
      size="sm"
      variation="success"
      data-test-id="progress-bar"
      :show-track="false"
    />
  </div>
</template>

<script>
/**
 * This component allow users to scan barcode.
 * The input element will inherit all the component attributes and listener,
 * so you can use this component as if it were a native html input element.
 */

import barcodeImage from '@public/media/images/barcode.png';
import SbProgressBar from '@src/components/ProgressBar/SbProgressBar.vue';

export default {
  name: 'SbBarcodeInputProgress',
  components: {
    SbProgressBar,
  },
  inheritAttrs: false,
  props: {
    /**
     * This prop is only valid to support v-model directive, it should not be used in the parent component.
     */
    modelValue: {
      type: String,
      default: '',
    },
    /**
     * Prop used to set a value from parent component.
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * Prop used only for testing purpose, which is assigned to wrapper element.
     */
    dataSbAutomation: {
      type: String,
      default: '',
    },
    /**
     * The value to show under the quantity label.
     */
    quantityValue: {
      type: Number,
      default: 0,
    },
    /**
     * The label to show over the quantity value.
     */
    quantityLabel: {
      type: String,
      default: '',
    },
    /**
     * Value between 0 and 1 that represents the progress of the scan.
     */
    progress: {
      type: Number,
      default: 0,
    },
  },
  emits: [
    /**
     * This event it only to support v-model directive, so the parent component should not subscribe.
     */
    'update:modelValue',
    /**
     * Emit input value when the enter key is pressed.
     */
    'enter',
  ],
  data: () => ({
    barcodeImage,
  }),
  methods: {
    /**
     * Method that allow to set focus in the input element.
     * Usefull when you want to set focus from the parent.
     */
    focus() {
      this.$refs.input.focus();
    },
    updateValue(event) {
      this.$emit('update:modelValue', event.target.value);
    },
    onPressEnter(event) {
      this.$emit('enter', event.target.value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@src/components/Input/style/sb-text-input.scss';

.sb-barcode-input-progress {
  position: relative;
}

.quantity-label {
  font-size: 0.625rem;
  position: absolute;
  left: $space-l;
  top: $space-xxs;
}

.quantity-value {
  font-size: $size-s;
  position: absolute;
  left: $space-l;
  bottom: 0.1rem;
}

input::placeholder {
  font-style: normal;
}

input {
  background-repeat: no-repeat;
  background-position: left 0.875rem center;
  padding-left: 10rem;
  padding-right: 0.875rem;
  width: 100%;

  &:hover,
  &:focus {
    padding-left: 10rem - calc($space-xxxs / 2);
    padding-right: 0.8rem;
    background-position: left 0.8rem center;
  }
}

.sb-progress-bar {
  position: absolute;
  bottom: -3px;
  width: 100%;
}
</style>
